import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, X } from 'lucide-react';
import { getProducts, getCategories, getBrands, getFavoriteIds } from '../lib/api';
import { useAuthStore } from '../lib/authStore';
import ProductCard from '../components/product/ProductCard';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [categoria, setCategoria] = useState(searchParams.get('categoria') || '');
  const [marca, setMarca] = useState(searchParams.get('marca') || '');
  const [ordenar, setOrdenar] = useState('nome-asc');
  const [showFilters, setShowFilters] = useState(false);
  const { isAuthenticated } = useAuthStore();

  const promocao = searchParams.get('promocao') || '';
  const destaque = searchParams.get('destaque') || '';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [prodRes, catRes, brandRes] = await Promise.all([
          getProducts({ search: searchParams.get('search') || '', categoria: searchParams.get('categoria') || '', marca: searchParams.get('marca') || '', promocao, destaque, limit: 500 }),
          getCategories(),
          getBrands(),
        ]);
        setProducts(prodRes.data.products || []);
        setTotal(prodRes.data.total || 0);
        setCategories(catRes.data || []);
        setBrands(brandRes.data || []);
        if (isAuthenticated) {
          try { const favRes = await getFavoriteIds(); setFavoriteIds(favRes.data || []); } catch(e) {}
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [searchParams, isAuthenticated, promocao, destaque]);

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (categoria) params.set('categoria', categoria);
    if (marca) params.set('marca', marca);
    if (promocao) params.set('promocao', promocao);
    if (destaque) params.set('destaque', destaque);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch(''); setCategoria(''); setMarca('');
    setSearchParams({});
  };

  const sorted = [...products].sort((a, b) => {
    const pa = a.isPromocao && a.precoPromocional ? a.precoPromocional : a.precoVenda;
    const pb = b.isPromocao && b.precoPromocional ? b.precoPromocional : b.precoVenda;
    switch (ordenar) {
      case 'preco-asc': return pa - pb;
      case 'preco-desc': return pb - pa;
      case 'nome-desc': return b.nome.localeCompare(a.nome);
      default: return a.nome.localeCompare(b.nome);
    }
  });

  const toggleFav = (id) => {
    setFavoriteIds(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B]">
      <Header />
      <main className="flex-1 py-6 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h1 className="text-3xl md:text-5xl font-bold text-white uppercase tracking-tight" style={{ fontFamily: 'Teko, sans-serif' }} data-testid="products-title">
              {promocao ? 'Promoções' : destaque ? 'Destaques' : 'Catálogo'}
            </h1>
            <p className="text-zinc-500 text-sm mt-1">{total} produto{total !== 1 ? 's' : ''}</p>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 p-4 mb-6">
            <div className="flex gap-3 mb-3">
              <div className="flex-1 relative">
                <input type="text" placeholder="Buscar por nome ou código..." value={search} onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                  className="w-full px-4 py-3 pl-10 bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:outline-none focus:border-red-500 text-sm" data-testid="products-search" />
                <Search className="absolute left-3 top-3.5 h-4 w-4 text-zinc-500" />
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className="md:hidden bg-red-600 hover:bg-red-700 text-white px-4 py-3 font-bold text-sm" data-testid="toggle-filters-btn">
                <Filter className="h-5 w-5" />
              </button>
            </div>

            <div className={`${showFilters ? 'block' : 'hidden'} md:block`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <select value={categoria} onChange={(e) => setCategoria(e.target.value)} className="px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm focus:outline-none focus:border-red-500" data-testid="filter-category">
                  <option value="">Todas as categorias</option>
                  {categories.map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
                </select>
                <select value={marca} onChange={(e) => setMarca(e.target.value)} className="px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm focus:outline-none focus:border-red-500" data-testid="filter-brand">
                  <option value="">Todas as marcas</option>
                  {brands.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}
                </select>
                <select value={ordenar} onChange={(e) => setOrdenar(e.target.value)} className="px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm focus:outline-none focus:border-red-500" data-testid="filter-sort">
                  <option value="nome-asc">Nome (A-Z)</option>
                  <option value="nome-desc">Nome (Z-A)</option>
                  <option value="preco-asc">Menor preço</option>
                  <option value="preco-desc">Maior preço</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button onClick={applyFilters} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 font-bold text-sm uppercase tracking-wider flex items-center gap-2" data-testid="apply-filters-btn">
                  <Filter className="h-4 w-4" /> Filtrar
                </button>
                <button onClick={clearFilters} className="bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-2.5 font-bold text-sm uppercase tracking-wider flex items-center gap-2" data-testid="clear-filters-btn">
                  <X className="h-4 w-4" /> Limpar
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => <div key={i} className="bg-zinc-900 border border-zinc-800 aspect-square animate-pulse" />)}
            </div>
          ) : sorted.length > 0 ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="products-grid">
              {sorted.map((p) => <ProductCard key={p.id} product={p} isFavorite={favoriteIds.includes(p.id)} onFavoriteToggle={toggleFav} />)}
            </div>
          ) : (
            <div className="text-center py-16"><p className="text-zinc-500 text-lg">Nenhum produto encontrado.</p></div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
